/**
 * Pure view-model of the tier set tracker (DOM-free, unit-tested in setsModel.test.ts): the 5
 * tier slots per hero, the 2/4-piece bonus rows (lit, or a ghost "1 piece away" preview), where
 * Altar Sigils come from (daily, first clears per dungeon bracket, vault, weekly ritual), and
 * which items the Catalyst can convert.
 */
import type { ClassDef, ClassId } from '@keystone/sim';
import {
  type Item,
  type Profile,
  type ScaleGrade,
  type TierSlot,
  SCALE_GRADES,
  SET_THRESHOLDS,
  TIER_SLOTS,
  VAULT_EVERY,
  WEEKLY_RITUAL_RUNS,
  catalystCost,
  dailyStatus,
  equippedGear,
  gradeForKey,
  isLocalDate,
  isoWeek,
  dayNumber,
  itemOwner,
  setBonusRows,
  setPieces,
  tierSetFor,
  vaultPending,
  weeklyReady,
} from '@keystone/progression';
import { SCALE_LABEL, shortfallText } from '../forge/resourceText';
import { SLOT_LABEL } from '../ui/itemText';

export interface SetSlotView {
  slot: TierSlot;
  label: string;
  item: Item | null;
  /** The equipped item is this hero's tier piece. */
  isPiece: boolean;
  pieceName: string;
}

export interface SetBonusView {
  pieces: number;
  name: string;
  description: string;
  active: boolean;
  /** Pieces still needed (0 when active). */
  away: number;
  /** "Active", "1 piece away", "3 pieces away". */
  status: string;
  /** The designed (rotation-rework) bonus, or the stand-in on today's kit. */
  variant: 'rework' | 'interim';
}

export interface SetTrackerView {
  classId: ClassId;
  setId: string;
  name: string;
  pieces: number;
  slots: SetSlotView[];
  bonuses: SetBonusView[];
  /** The next bonus not yet active (goal gradient), or null when all are lit. */
  next: SetBonusView | null;
}

const awayText = (n: number): string => (n <= 0 ? 'Active' : `${n} piece${n === 1 ? '' : 's'} away`);

/** One hero's set card. `def` is the hero's (base) class def, for which bonus text applies. */
export function setTracker(p: Profile, classId: ClassId, def: ClassDef | undefined): SetTrackerView | null {
  const set = tierSetFor(classId);
  if (!set) return null;
  const gear = equippedGear(p, classId);
  const pieces = setPieces(gear, classId);
  const slots = TIER_SLOTS.map((slot) => {
    const item = gear[slot] ?? null;
    return { slot, label: SLOT_LABEL[slot], item, isPiece: !!item && item.setId === set.id, pieceName: set.pieceNames[slot] };
  });
  const rows = def
    ? setBonusRows(def)
    : SET_THRESHOLDS.map((n) => ({ pieces: n, variant: 'rework' as const, text: set.text[n]! }));
  const bonuses = rows.map((r) => {
    const away = Math.max(0, r.pieces - pieces);
    return { pieces: r.pieces, name: r.text.name, description: r.text.description, active: away === 0, away, status: awayText(away), variant: r.variant };
  });
  return { classId, setId: set.id, name: set.name, pieces, slots, bonuses, next: bonuses.find((b) => !b.active) ?? null };
}

// ---------------------------------------------------------------------------
// Where Sigils come from
// ---------------------------------------------------------------------------

export interface BracketView {
  grade: ScaleGrade;
  label: string;
  /** "+2–4". */
  keys: string;
  cleared: boolean;
}

export interface SigilSourcesView {
  sigils: number;
  daily: { ready: boolean; sigils: number; label: string; detail: string } | null;
  brackets: { dungeonId: string; name: string; grades: BracketView[]; cleared: number }[];
  vault: { ready: boolean; runsLeft: number; label: string };
  weekly: { runs: number; need: number; ready: boolean; claimed: boolean; label: string } | null;
}

const BRACKET_KEYS: Readonly<Record<ScaleGrade, string>> = { ashen: '+2–4', jade: '+5–8', gilded: '+9–12', eternal: '+13+' };

export function sigilSources(p: Profile, localDate: string | undefined, dungeons: readonly { id: string; name: string }[]): SigilSourcesView {
  const date = localDate && isLocalDate(localDate) ? localDate : undefined;
  let daily: SigilSourcesView['daily'] = null;
  if (date) {
    const d = dailyStatus(p.forge, date);
    daily = d.ready
      ? { ready: true, sigils: d.sigils, label: `Ready: ${d.sigils} Sigil${d.sigils === 1 ? '' : 's'}`, detail: d.sigils > 1 ? `${d.sigils} days banked. Your first completed run today claims them.` : 'Your first completed run today claims it (timed or not).' }
      : { ready: false, sigils: 0, label: 'Claimed today', detail: 'Comes back tomorrow. Missed days bank up to 3, so nothing is lost.' };
  }
  const brackets = dungeons.map((d) => {
    const done = p.forge.brackets[d.id] ?? [];
    const grades = SCALE_GRADES.map((g) => ({ grade: g, label: SCALE_LABEL[g], keys: BRACKET_KEYS[g], cleared: done.includes(g) }));
    return { dungeonId: d.id, name: d.name, grades, cleared: grades.filter((g) => g.cleared).length };
  });
  const ready = vaultPending(p);
  const runsLeft = Math.max(0, VAULT_EVERY - p.vault.runsInWindow);
  const vault = { ready, runsLeft, label: ready ? 'Vault ready: the Altar Cache holds a Sigil' : `Altar Cache in ${runsLeft} run${runsLeft === 1 ? '' : 's'}` };
  let weekly: SigilSourcesView['weekly'] = null;
  if (date) {
    const w = p.forge.weekly;
    const sameWeek = w.isoWeek === isoWeek(dayNumber(date));
    const runs = sameWeek ? w.runs : 0;
    const claimed = sameWeek && w.claimed;
    const isReady = weeklyReady(w, date);
    weekly = { runs: Math.min(runs, WEEKLY_RITUAL_RUNS), need: WEEKLY_RITUAL_RUNS, ready: isReady, claimed, label: claimed ? 'Ritual claimed this week' : isReady ? 'Ritual ready: claim a Sigil' : `Weekly Ritual ${Math.min(runs, WEEKLY_RITUAL_RUNS)}/${WEEKLY_RITUAL_RUNS} runs` };
  }
  return { sigils: p.wallet.sigils, daily, brackets, vault, weekly };
}

/** "Time a +5 on Venom Altar for the Jade bracket Sigil" (the cheapest uncleared bracket), or null. */
export function nextBracketHint(p: Profile, dungeons: readonly { id: string; name: string }[], maxKey: number): string | null {
  for (const g of SCALE_GRADES) {
    for (const d of dungeons) {
      if ((p.forge.brackets[d.id] ?? []).includes(g)) continue;
      const min = g === 'ashen' ? 2 : g === 'jade' ? 5 : g === 'gilded' ? 9 : 13;
      if (min > maxKey) return null;
      return `Time a +${min} on ${d.name} for the ${SCALE_LABEL[g]} bracket Sigil.`;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Catalyst
// ---------------------------------------------------------------------------

export interface CatalystOption {
  item: Item;
  equipped: boolean;
  enabled: boolean;
  reason: string | null;
}

/**
 * Items the Catalyst can turn into `classId`'s tier piece: worn by the hero (or unequipped in the
 * bag), in a tier slot, not a legendary, not already this set's piece.
 */
export function catalystOptions(p: Profile, classId: ClassId): CatalystOption[] {
  const set = tierSetFor(classId);
  if (!set) return [];
  const gear = equippedGear(p, classId);
  const cost = catalystCost();
  const short = shortfallText(missing(p, cost));
  const out: CatalystOption[] = [];
  const seen = new Set<string>();
  const consider = (item: Item, equipped: boolean): void => {
    if (seen.has(item.id) || item.legendaryId || item.setId === set.id || !(TIER_SLOTS as readonly string[]).includes(item.slot)) return;
    seen.add(item.id);
    out.push({ item, equipped, enabled: !short, reason: short });
  };
  for (const s of TIER_SLOTS) if (gear[s]) consider(gear[s]!, true);
  for (const it of p.inventory) if (!itemOwner(p, it.id)) consider(it, false);
  return out.sort((a, b) => Number(b.equipped) - Number(a.equipped) || b.item.ilvl - a.item.ilvl);
}

function missing(p: Profile, cost: { sigils?: number; fangsteel?: number }): { sigils?: number; fangsteel?: number } {
  const out: { sigils?: number; fangsteel?: number } = {};
  if ((cost.sigils ?? 0) > p.wallet.sigils) out.sigils = (cost.sigils ?? 0) - p.wallet.sigils;
  if ((cost.fangsteel ?? 0) > p.wallet.fangsteel) out.fangsteel = (cost.fangsteel ?? 0) - p.wallet.fangsteel;
  return out;
}

/** Grade bracket a key level belongs to (for "first clear" callouts). */
export const bracketOf = (level: number): ScaleGrade => gradeForKey(level);
